import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { 
  Users, UserPlus, Building2, TrendingUp, Award,
  Mail, Phone, ChevronRight, Search, Filter,
  CheckCircle, Clock, XCircle, MoreVertical,
  Edit2, Trash2, UserCheck, Shield, Crown,
  BarChart3, Target, Wallet
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import BrokerSidebar from '@/components/broker/BrokerSidebar';
import { formatINR } from '@/utils/currency';
import { DashboardSkeleton, ErrorBanner } from '@/components/ui/loading-states';

const ROLE_CONFIG = {
  MASTER: { color: 'text-purple-400', bg: 'bg-purple-500/10', label: 'Master Broker', icon: Crown },
  TEAM_LEAD: { color: 'text-yellow-400', bg: 'bg-yellow-500/10', label: 'Team Lead', icon: Shield },
  SENIOR: { color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'Senior', icon: Award },
  JUNIOR: { color: 'text-gray-400', bg: 'bg-gray-500/10', label: 'Junior', icon: Users },
};

const STATUS_CONFIG = {
  PENDING_APPROVAL: { color: 'text-yellow-400', bg: 'bg-yellow-500/10', label: 'Pending' },
  APPROVED: { color: 'text-[#4ADE80]', bg: 'bg-[#4ADE80]/10', label: 'Active' },
  REJECTED: { color: 'text-red-400', bg: 'bg-red-500/10', label: 'Rejected' },
  SUSPENDED: { color: 'text-orange-400', bg: 'bg-orange-500/10', label: 'Suspended' },
};

export default function BrokerTeamManagement() {
  const [subBrokers, setSubBrokers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [activeView, setActiveView] = useState('members'); // 'members' | 'teams' | 'analytics'
  
  const [newSubBroker, setNewSubBroker] = useState({
    email: '',
    name: '',
    phone: '',
    role: 'JUNIOR',
    commissionSharePercent: 50
  });
  
  const [newTeam, setNewTeam] = useState({
    name: '',
    description: '',
    leaderId: ''
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [subBrokersRes, teamsRes, analyticsRes] = await Promise.all([
        api.get(`/broker/${user.id}/sub-brokers`),
        api.get(`/broker/${user.id}/teams`),
        api.get(`/broker/${user.id}/team-analytics`)
      ]);
      
      setSubBrokers(subBrokersRes.data.subBrokers || []);
      setTeams(teamsRes.data.teams || []);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load team data. Please try again.');
      setSubBrokers([]);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubBroker = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post(`/broker/${user.id}/sub-brokers`, newSubBroker);
      toast.success(`Sub-broker created! Temporary password: ${response.data.tempPassword}`, {
        duration: 10000
      });
      setShowAddModal(false);
      setNewSubBroker({ email: '', name: '', phone: '', role: 'JUNIOR', commissionSharePercent: 50 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create sub-broker');
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/broker/${user.id}/teams`, newTeam);
      toast.success('Team created successfully');
      setShowTeamModal(false);
      setNewTeam({ name: '', description: '', leaderId: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    }
  };

  const handleRemoveSubBroker = async (subBrokerId) => {
    if (!confirm('Are you sure you want to remove this sub-broker?')) return;
    
    try {
      await api.delete(`/broker/${user.id}/sub-brokers/${subBrokerId}`);
      toast.success('Sub-broker removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove sub-broker');
    }
  };

  const filteredSubBrokers = subBrokers.filter(sb => {
    const matchesSearch = !searchQuery || 
      sb.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sb.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTeam = selectedTeam === 'all' || sb.teamId === selectedTeam;
    return matchesSearch && matchesTeam;
  });

  const stats = {
    total: subBrokers.length,
    active: subBrokers.filter(sb => sb.status === 'APPROVED').length,
    pending: subBrokers.filter(sb => sb.status === 'PENDING_APPROVAL').length,
    totalCapital: subBrokers.reduce((sum, sb) => sum + (sb.stats?.totalCapitalRaised || 0), 0)
  };

  return (
    <div className="min-h-screen bg-black flex">
      <BrokerSidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-6 md:p-10">
        {error && <ErrorBanner message={error} onRetry={fetchData} />}
        {loading ? <DashboardSkeleton cards={4} rows={4} /> : (<>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs text-gray-600 uppercase tracking-widest mb-2">Enterprise</p>
            <h1 className="text-2xl font-light text-white">Team Management</h1>
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={() => setShowTeamModal(true)}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Create Team
            </Button>
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-[#4ADE80] text-black hover:bg-[#3fcf70]"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Sub-Broker
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-white/5 border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <Users className="w-5 h-5 text-gray-600" />
              <span className="text-xs text-gray-600">TOTAL</span>
            </div>
            <p className="text-3xl font-light text-white">{stats.total}</p>
            <p className="text-xs text-gray-600 mt-1">Sub-brokers</p>
          </div>
          
          <div className="bg-[#4ADE80]/5 border border-[#4ADE80]/20 p-5">
            <div className="flex items-center justify-between mb-3">
              <CheckCircle className="w-5 h-5 text-[#4ADE80]" />
              <span className="text-xs text-[#4ADE80]">ACTIVE</span>
            </div>
            <p className="text-3xl font-light text-[#4ADE80]">{stats.active}</p>
            <p className="text-xs text-gray-600 mt-1">Approved members</p>
          </div>
          
          <div className="bg-yellow-500/5 border border-yellow-500/20 p-5">
            <div className="flex items-center justify-between mb-3">
              <Clock className="w-5 h-5 text-yellow-500" />
              <span className="text-xs text-yellow-500">PENDING</span>
            </div>
            <p className="text-3xl font-light text-yellow-500">{stats.pending}</p>
            <p className="text-xs text-gray-600 mt-1">Awaiting approval</p>
          </div>
          
          <div className="bg-white/5 border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <TrendingUp className="w-5 h-5 text-gray-600" />
              <span className="text-xs text-gray-600">CAPITAL</span>
            </div>
            <p className="text-3xl font-light text-white">{formatINR(stats.totalCapital)}</p>
            <p className="text-xs text-gray-600 mt-1">Team's total raised</p>
          </div>
        </div>

        {/* View Tabs */}
        <div className="flex gap-1 mb-6 border-b border-white/10">
          {[
            { id: 'members', label: 'Team Members', icon: Users },
            { id: 'teams', label: 'Teams', icon: Building2 },
            { id: 'analytics', label: 'Analytics', icon: BarChart3 }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveView(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 text-sm transition-all border-b-2 -mb-px ${
                  activeView === tab.id
                    ? 'text-[#4ADE80] border-[#4ADE80]'
                    : 'text-gray-500 border-transparent hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Members View */}
        {activeView === 'members' && (
          <>
            {/* Filters */}
            <div className="flex items-center gap-4 mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or email..."
                  className="pl-10 bg-white/5 border-white/10 text-white"
                />
              </div>
              
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="bg-white/5 border border-white/10 text-white text-sm px-4 py-2 rounded"
              >
                <option value="all">All Teams</option>
                {teams.map(team => (
                  <option key={team.teamId} value={team.teamId}>{team.name}</option>
                ))}
              </select>
            </div>

            {/* Sub-Brokers List */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-[#4ADE80] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredSubBrokers.length === 0 ? (
              <div className="text-center py-20 bg-white/5 border border-white/10">
                <Users className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">No sub-brokers yet</p>
                <p className="text-gray-600 text-sm mb-6">Start building your team by adding sub-brokers</p>
                <Button
                  onClick={() => setShowAddModal(true)}
                  className="bg-[#4ADE80] text-black hover:bg-[#3fcf70]"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add First Sub-Broker
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredSubBrokers.map((sb) => {
                  const RoleIcon = ROLE_CONFIG[sb.brokerRole]?.icon || Users;
                  return (
                    <motion.div
                      key={sb.userId}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white/5 border border-white/10 p-5 hover:border-white/20 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {/* Avatar */}
                          <div className="w-12 h-12 bg-[#4ADE80]/20 rounded-full flex items-center justify-center">
                            <span className="text-[#4ADE80] font-semibold">
                              {sb.fullName?.charAt(0) || 'S'}
                            </span>
                          </div>
                          
                          {/* Info */}
                          <div>
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className="text-white font-medium">{sb.fullName}</h3>
                              <span className={`px-2 py-0.5 rounded text-xs flex items-center gap-1 ${ROLE_CONFIG[sb.brokerRole]?.bg} ${ROLE_CONFIG[sb.brokerRole]?.color}`}>
                                <RoleIcon className="w-3 h-3" />
                                {ROLE_CONFIG[sb.brokerRole]?.label}
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs ${STATUS_CONFIG[sb.status]?.bg} ${STATUS_CONFIG[sb.status]?.color}`}>
                                {STATUS_CONFIG[sb.status]?.label}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" /> {sb.email}
                              </span>
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {sb.phone}
                              </span>
                              {sb.teamName && (
                                <span className="flex items-center gap-1">
                                  <Building2 className="w-3 h-3" /> {sb.teamName}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Stats & Actions */}
                        <div className="flex items-center gap-8">
                          {sb.status === 'APPROVED' && (
                            <div className="flex gap-6 text-sm">
                              <div className="text-center">
                                <p className="text-white font-mono">{formatINR(sb.stats?.totalCapitalRaised || 0)}</p>
                                <p className="text-gray-600 text-xs">Raised</p>
                              </div>
                              <div className="text-center">
                                <p className="text-[#4ADE80] font-mono">{formatINR(sb.stats?.totalCommission || 0)}</p>
                                <p className="text-gray-600 text-xs">Commission</p>
                              </div>
                              <div className="text-center">
                                <p className="text-white font-mono">{sb.stats?.activeInvestors || 0}</p>
                                <p className="text-gray-600 text-xs">Investors</p>
                              </div>
                            </div>
                          )}
                          
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-white/20 text-white hover:bg-white/10"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveSubBroker(sb.userId)}
                              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Teams View */}
        {activeView === 'teams' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {teams.length === 0 ? (
              <div className="col-span-2 text-center py-20 bg-white/5 border border-white/10">
                <Building2 className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">No teams created yet</p>
                <p className="text-gray-600 text-sm mb-6">Organize your sub-brokers into teams</p>
                <Button
                  onClick={() => setShowTeamModal(true)}
                  className="bg-[#4ADE80] text-black hover:bg-[#3fcf70]"
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Create First Team
                </Button>
              </div>
            ) : (
              teams.map(team => (
                <div key={team.teamId} className="bg-white/5 border border-white/10 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg text-white font-medium">{team.name}</h3>
                      {team.description && (
                        <p className="text-sm text-gray-500 mt-1">{team.description}</p>
                      )}
                    </div>
                    <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-4 mb-4">
                    {team.leaderName ? (
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm text-gray-400">Led by <span className="text-white">{team.leaderName}</span></span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">No team lead assigned</span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
                    <div>
                      <p className="text-2xl font-light text-white">{team.memberCount || 0}</p>
                      <p className="text-xs text-gray-600">Members</p>
                    </div>
                    <div>
                      <p className="text-2xl font-light text-white">{formatINR(team.stats?.totalCapitalRaised || 0)}</p>
                      <p className="text-xs text-gray-600">Capital</p>
                    </div>
                    <div>
                      <p className="text-2xl font-light text-[#4ADE80]">{formatINR(team.stats?.totalCommission || 0)}</p>
                      <p className="text-xs text-gray-600">Commission</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Analytics View */}
        {activeView === 'analytics' && analytics && (
          <div className="space-y-6">
            {/* Overview */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white/5 border border-white/10 p-5">
                <p className="text-xs text-gray-600 uppercase tracking-wider mb-2">Combined Capital</p>
                <p className="text-2xl font-light text-white">{formatINR(analytics.overview?.combinedCapitalRaised || 0)}</p>
              </div>
              <div className="bg-white/5 border border-white/10 p-5">
                <p className="text-xs text-gray-600 uppercase tracking-wider mb-2">Combined Commission</p>
                <p className="text-2xl font-light text-[#4ADE80]">{formatINR(analytics.overview?.combinedCommission || 0)}</p>
              </div>
              <div className="bg-white/5 border border-white/10 p-5">
                <p className="text-xs text-gray-600 uppercase tracking-wider mb-2">Total Investors</p>
                <p className="text-2xl font-light text-white">{analytics.overview?.totalInvestors || 0}</p>
              </div>
              <div className="bg-white/5 border border-white/10 p-5">
                <p className="text-xs text-gray-600 uppercase tracking-wider mb-2">Conversion Rate</p>
                <p className="text-2xl font-light text-white">{analytics.conversionFunnel?.conversionRate || 0}%</p>
              </div>
            </div>
            
            {/* Top Performers */}
            <div className="bg-white/5 border border-white/10 p-6">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-[#4ADE80]" />
                Top Performers
              </h3>
              
              {analytics.topPerformers?.length > 0 ? (
                <div className="space-y-3">
                  {analytics.topPerformers.map((performer, index) => (
                    <div key={performer.userId} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                      <div className="flex items-center gap-4">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          index === 0 ? 'bg-yellow-500/20 text-yellow-500' :
                          index === 1 ? 'bg-gray-400/20 text-gray-400' :
                          index === 2 ? 'bg-amber-600/20 text-amber-600' :
                          'bg-white/10 text-white'
                        }`}>
                          {index + 1}
                        </span>
                        <div>
                          <p className="text-white">{performer.name}</p>
                          <p className="text-xs text-gray-500">{ROLE_CONFIG[performer.role]?.label || performer.role}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-mono">{formatINR(performer.capitalRaised)}</p>
                        <p className="text-xs text-[#4ADE80]">{formatINR(performer.commission)} commission</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No performance data yet</p>
              )}
            </div>
            
            {/* Team Breakdown */}
            {analytics.teamBreakdown?.length > 0 && (
              <div className="bg-white/5 border border-white/10 p-6">
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#4ADE80]" />
                  Team Performance
                </h3>
                
                <div className="space-y-3">
                  {analytics.teamBreakdown.map(team => (
                    <div key={team.teamId} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                      <div>
                        <p className="text-white">{team.teamName}</p>
                        <p className="text-xs text-gray-500">{team.memberCount} members • Led by {team.leaderName || 'TBD'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-mono">{formatINR(team.totalCapitalRaised)}</p>
                        <p className="text-xs text-[#4ADE80]">{formatINR(team.totalCommission)} earned</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        </>)}
      </main>

      {/* Add Sub-Broker Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0a0a0a] border border-white/10 rounded-xl w-full max-w-lg p-6"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-xl text-white mb-6 flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-[#4ADE80]" />
                Add Sub-Broker
              </h3>
              
              <form onSubmit={handleAddSubBroker} className="space-y-4">
                <div>
                  <label className="text-sm text-gray-500 block mb-2">Full Name *</label>
                  <Input
                    value={newSubBroker.name}
                    onChange={(e) => setNewSubBroker({...newSubBroker, name: e.target.value})}
                    placeholder="Enter full name"
                    className="bg-white/5 border-white/10 text-white"
                    required
                  />
                </div>
                
                <div>
                  <label className="text-sm text-gray-500 block mb-2">Email *</label>
                  <Input
                    type="email"
                    value={newSubBroker.email}
                    onChange={(e) => setNewSubBroker({...newSubBroker, email: e.target.value})}
                    placeholder="Enter email address"
                    className="bg-white/5 border-white/10 text-white"
                    required
                  />
                </div>
                
                <div>
                  <label className="text-sm text-gray-500 block mb-2">Phone *</label>
                  <Input
                    value={newSubBroker.phone}
                    onChange={(e) => setNewSubBroker({...newSubBroker, phone: e.target.value})}
                    placeholder="Enter phone number"
                    className="bg-white/5 border-white/10 text-white"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-500 block mb-2">Role</label>
                    <select
                      value={newSubBroker.role}
                      onChange={(e) => setNewSubBroker({...newSubBroker, role: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 text-white px-3 py-2 rounded"
                    >
                      <option value="JUNIOR">Junior</option>
                      <option value="SENIOR">Senior</option>
                      <option value="TEAM_LEAD">Team Lead</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-500 block mb-2">Commission Share %</label>
                    <Input
                      type="number"
                      min="10"
                      max="90"
                      value={newSubBroker.commissionSharePercent}
                      onChange={(e) => setNewSubBroker({...newSubBroker, commissionSharePercent: parseInt(e.target.value)})}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>
                
                <p className="text-xs text-gray-600">
                  The sub-broker will receive {newSubBroker.commissionSharePercent}% of the commission you earn on their deals.
                </p>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 border-white/20 text-white hover:bg-white/10"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-[#4ADE80] text-black hover:bg-[#3fcf70]"
                  >
                    Add Sub-Broker
                  </Button>
                </div>
                
                <p className="text-xs text-yellow-500 text-center">
                  Note: Sub-brokers require admin approval before they can start working.
                </p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Team Modal */}
      <AnimatePresence>
        {showTeamModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowTeamModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0a0a0a] border border-white/10 rounded-xl w-full max-w-lg p-6"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-xl text-white mb-6 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#4ADE80]" />
                Create Team
              </h3>
              
              <form onSubmit={handleCreateTeam} className="space-y-4">
                <div>
                  <label className="text-sm text-gray-500 block mb-2">Team Name *</label>
                  <Input
                    value={newTeam.name}
                    onChange={(e) => setNewTeam({...newTeam, name: e.target.value})}
                    placeholder="e.g., South Mumbai Team"
                    className="bg-white/5 border-white/10 text-white"
                    required
                  />
                </div>
                
                <div>
                  <label className="text-sm text-gray-500 block mb-2">Description</label>
                  <textarea
                    value={newTeam.description}
                    onChange={(e) => setNewTeam({...newTeam, description: e.target.value})}
                    placeholder="Team focus area or description"
                    className="w-full bg-white/5 border border-white/10 text-white px-3 py-2 rounded h-24"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-gray-500 block mb-2">Team Lead (Optional)</label>
                  <select
                    value={newTeam.leaderId}
                    onChange={(e) => setNewTeam({...newTeam, leaderId: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 text-white px-3 py-2 rounded"
                  >
                    <option value="">Select a team lead</option>
                    {subBrokers.filter(sb => sb.status === 'APPROVED').map(sb => (
                      <option key={sb.userId} value={sb.userId}>{sb.fullName}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowTeamModal(false)}
                    className="flex-1 border-white/20 text-white hover:bg-white/10"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-[#4ADE80] text-black hover:bg-[#3fcf70]"
                  >
                    Create Team
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
